import type { CSSProperties } from "react";
import {
  IP_BLOCKING_SERIES_CONTRACT,
  motionClass,
  useAnimatedNumber,
  useWidgetMotion,
} from "@nexus/widget-kit";
import type { IpBlockingSeriesData } from "@nexus/widget-kit";
import {
  firstError,
  MetricDetail,
  MetricLabel,
  MetricValue,
  unwrap,
  Widget,
  WidgetSkeleton,
  WidgetState,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";

export interface SafeBlockingSummaryConfig {
  title?: string;
  animateValue?: boolean;
  /** Rows under the headline: Web/IPTV safety (default) or the regulator-approved share. */
  breakdown?: "channels" | "approved";
  /** Safe counts under the bars of the Web and IPTV breakdown. */
  showValues?: boolean;
}

export const BREAKDOWNS = [
  { value: "channels", label: "Web and IPTV safety" },
  { value: "approved", label: "Regulator approved" },
];

export const manifest: WidgetManifest = {
  id: "safe-to-block",
  name: "Safe to Block",
  version: "1.0.0",
  description:
    "Distinct detected or active IPs or domains and Safe to Block ratios, broken down by Web and IPTV or by regulator approval. Global safety is the primary figure.",
  dataRequests: {
    safety: {
      source: "safeip-summary",
      contract: IP_BLOCKING_SERIES_CONTRACT,
      configurable: true,
      sources: ["safeip-summary", "safe-domain-summary"],
    },
  },
};

export const defaultConfig: SafeBlockingSummaryConfig = { title: "Safe to Block" };
const count = new Intl.NumberFormat("en");
const ratio = new Intl.NumberFormat("en", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const percent = (value: number | null | undefined) =>
  value == null ? "Unavailable" : ratio.format(value);

/** Presentation threshold for a high share, not a validated business threshold. */
const SAFE_SHARE_HIGH = 0.8;

interface SafeShareAssessment {
  text: string;
  fill: string;
}

const UNAVAILABLE: SafeShareAssessment = {
  text: "text-muted",
  fill: "bg-border-strong",
};

function assessSafeShare(value: number | null | undefined): SafeShareAssessment {
  if (value == null) return UNAVAILABLE;
  if (value < 0.2)
    return {
      text: "text-(--safe-share-very-low-text)",
      fill: "bg-(--safe-share-very-low-fill)",
    };
  if (value < 0.5)
    return {
      text: "text-(--safe-share-low-text)",
      fill: "bg-(--safe-share-low-fill)",
    };
  if (value < SAFE_SHARE_HIGH)
    return {
      text: "text-(--safe-share-moderate-text)",
      fill: "bg-(--safe-share-moderate-fill)",
    };
  return {
    text: "text-(--safe-share-high-text)",
    fill: "bg-(--safe-share-high-fill)",
  };
}

/** Fill colored by level over a neutral track. */
function SafetyBar({
  value,
  shown,
  fill,
  thick,
  ariaLabel,
}: {
  value: number;
  shown: number;
  fill: string;
  thick?: boolean;
  ariaLabel?: string;
}) {
  return (
    <div
      className={`overflow-hidden rounded-full bg-border ${thick ? "h-3" : "h-1.5"}`}
      role="img"
      aria-label={
        ariaLabel ??
        `Safe to block ${percent(value)}; not confirmed safe to block ${percent(1 - value)}`
      }
    >
      <div
        className={`h-full rounded-full ${fill}`}
        style={{ width: `${Math.min(1, Math.max(0, shown)) * 100}%` }}
      />
    </div>
  );
}

function ChannelRatio({
  label,
  value,
  detail,
  safeCount,
  stacked,
  motion,
  animateValue,
  delay,
  ariaLabel,
}: {
  label: string;
  value: number | null | undefined;
  detail?: string;
  /** Safe count shown on a line under the bar. */
  safeCount?: string;
  /** Full-width row with the percentage below the bar. */
  stacked?: boolean;
  motion: boolean;
  animateValue: boolean;
  delay: number;
  ariaLabel?: string;
}) {
  const shown = useAnimatedNumber(value, { duration: 1100, enabled: motion && animateValue });
  const assessment = assessSafeShare(value);
  const shownPercent = percent(value == null ? value : (shown ?? value));
  const percentClass = `font-bold tabular-nums ${value == null ? "text-metric-caption" : "text-xl"} ${assessment.text}`;
  return (
    <div
      className={`${motionClass(motion, "nx-rise")} ${stacked ? "col-span-full" : ""} grid min-w-0 grid-cols-[1fr_auto] items-baseline gap-x-3 gap-y-2`}
      style={{ "--nx-delay": `${delay}ms` } as CSSProperties}
    >
      <dt className="min-w-0 truncate text-metric-caption font-medium text-secondary">{label}</dt>
      {stacked ? (
        <dd className="text-metric-detail font-semibold tabular-nums text-secondary">{detail}</dd>
      ) : (
        <dd className={percentClass}>
          {shownPercent}
          {detail && (
            <span className="ml-1.5 text-metric-detail font-semibold text-secondary">{detail}</span>
          )}
        </dd>
      )}
      {value != null && (
        <dd className="col-span-2 wall-short:hidden">
          <SafetyBar
            value={value}
            shown={shown ?? value}
            fill={assessment.fill}
            ariaLabel={ariaLabel}
          />
        </dd>
      )}
      {stacked && <dd className={`col-span-2 ${percentClass}`}>{shownPercent}</dd>}
      {safeCount && (
        <dd className="col-span-2 flex items-center gap-1.5 text-metric-detail font-semibold tabular-nums text-secondary wall-short:hidden">
          <span aria-hidden className={`size-2 rounded-full ${assessment.fill}`} />
          {safeCount}
        </dd>
      )}
    </div>
  );
}

export function Component({ data, config }: WidgetProps<SafeBlockingSummaryConfig>) {
  const report = unwrap<IpBlockingSeriesData>(data.safety);
  const error = firstError(data, ["safety"]);
  const isDomain = report?.targetKind === "DOMAIN";
  const targets = isDomain ? "domains" : "IPs";
  const stage = report?.dateBasis === "blocking_window" ? null : "detected";
  const stagedTargets = stage ? `${stage} ${targets}` : targets;
  const distinctLabel = stage ? `Distinct ${targets} ${stage}` : `Distinct ${targets}`;
  const cohort = report?.cohorts?.total;
  // Unsafe targets are never submitted to the regulator: safe targets are the denominator.
  const safeTargets = report?.safety?.safe;
  const approvedRatio =
    cohort?.approved != null && safeTargets ? cohort.approved / safeTargets : null;
  const configuredTitle = config.title?.trim();
  const title =
    !configuredTitle || /^(?:Safe ?IP|Safe Domain) Summary$/i.test(configuredTitle)
      ? defaultConfig.title
      : configuredTitle;
  const safety = report?.safety;
  const showValues = config.showValues === true && config.breakdown !== "approved";
  const safeCount = (value: number | null | undefined) =>
    showValues
      ? `${value == null ? "Unavailable" : count.format(value)} ${targets} safe`
      : undefined;
  const assessment = assessSafeShare(safety?.ratio);
  const motion = useWidgetMotion();
  const animateValue = config.animateValue === true;
  const shownRatio = useAnimatedNumber(safety?.ratio, {
    duration: 1100,
    enabled: motion && animateValue,
  });
  const shownDetected = useAnimatedNumber(safety?.detected, {
    duration: 1100,
    enabled: motion && animateValue,
  });
  // On a short wall screen the detected total moves to a header pill (as on Top
  // Detected Hosts) instead of taking a line of its own under the headline.
  const detectedPill =
    report && safety?.detected != null ? (
      <span
        className="hidden rounded-full bg-surface-elevated px-2.5 py-0.5 text-metric-detail font-semibold tabular-nums text-secondary wall-short:inline"
        title={distinctLabel}
      >
        {count.format(Math.round(shownDetected ?? safety.detected))} {targets}
      </span>
    ) : undefined;
  return (
    <Widget title={title} actions={detectedPill} intrinsicHeight>
      {data.safety?.isLoading && !report ? (
        <WidgetSkeleton shape="kpi" />
      ) : error ? (
        <WidgetIssueState error={error} />
      ) : !report ? (
        <WidgetState title="No safety data" />
      ) : (
        <div className="density-metric-padding flex min-w-0 flex-1 flex-col gap-4 wall-short:gap-3">
          <div className="flex min-w-0 flex-wrap items-end justify-between gap-x-6 gap-y-3">
            <div className="min-w-0 wall-short:flex wall-short:flex-wrap wall-short:items-baseline wall-short:gap-x-3">
              <MetricValue size="headline">
                <span className={assessment.text}>
                  {safety?.ratio == null ? "—" : percent(shownRatio ?? safety.ratio)}
                </span>
              </MetricValue>
              <MetricLabel className="mt-1 wall-short:mt-0">
                {safety?.ratio == null ? (
                  "Safe to block ratio unavailable"
                ) : (
                  <>
                    <span className="wall-short:hidden">{`of ${stagedTargets} are safe to block`}</span>
                    <span className="hidden wall-short:inline">{`of ${stagedTargets}`}</span>
                  </>
                )}
              </MetricLabel>
            </div>
            <div className="min-w-0 text-right wall-short:hidden">
              <p className="text-2xl font-bold tabular-nums text-primary">
                {safety?.detected == null
                  ? "—"
                  : count.format(Math.round(shownDetected ?? safety.detected))}
              </p>
              <MetricDetail as="p">{distinctLabel}</MetricDetail>
            </div>
          </div>
          {safety?.ratio != null && (
            <div className="space-y-2">
              <SafetyBar
                value={safety.ratio}
                shown={shownRatio ?? safety.ratio}
                fill={assessment.fill}
                thick
              />
              {/* Repeats the headline: the first detail a short wall screen drops. */}
              <div className="flex flex-wrap justify-between gap-x-4 text-metric-detail wall-short:hidden">
                <span className="flex items-center gap-1.5 font-semibold text-secondary">
                  <span aria-hidden className={`size-2 rounded-full ${assessment.fill}`} />
                  {showValues
                    ? `Safe ${safety.safe == null ? "unavailable" : `${count.format(safety.safe)} ${targets}`}`
                    : `Safe ${percent(safety.ratio)}`}
                </span>
                {!showValues && (
                  <span className="flex items-center gap-1.5 text-muted">
                    <span aria-hidden className="size-2 rounded-full bg-border-strong" />
                    Not safe {percent(1 - safety.ratio)}
                  </span>
                )}
              </div>
            </div>
          )}
          <dl className="grid min-w-0 grid-cols-1 gap-x-6 gap-y-4 pt-2 wall-short:pt-0 @min-[22rem]/widget:grid-cols-2">
            {config.breakdown === "approved" ? (
              <ChannelRatio
                label="Regulator approved & sent for blocking"
                value={approvedRatio}
                detail={
                  cohort?.approved == null
                    ? undefined
                    : `${count.format(cohort.approved)} ${targets}`
                }
                stacked
                ariaLabel={`Regulator approved and sent for blocking ${percent(approvedRatio)} of safe ${stagedTargets}`}
                motion={motion}
                animateValue={animateValue}
                delay={160}
              />
            ) : (
              <>
                <ChannelRatio
                  label="Web"
                  value={report.platformSafety?.web}
                  safeCount={safeCount(report.platformSafeCounts?.web)}
                  motion={motion}
                  animateValue={animateValue}
                  delay={160}
                />
                <ChannelRatio
                  label="IPTV"
                  value={report.platformSafety?.iptv}
                  safeCount={safeCount(report.platformSafeCounts?.iptv)}
                  motion={motion}
                  animateValue={animateValue}
                  delay={240}
                />
              </>
            )}
          </dl>
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<SafeBlockingSummaryConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
